import type RouterService from '@ember/routing/router-service';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { IMPERSONATE_ACCOUNT_ID_KEY } from 'walden-fast/constants/local-storage-key';
import type { ImpersonationAccount } from 'walden-fast/types/fast-backend/account';
import type { ImpersonationPermission } from 'walden-fast/types/fast-backend/user';
import type CacheManagerService from './cache-manager';
import type FastApiService from './fast-api';
import type FilterService from './filter';
import type KpiService from './kpi';
import type PowerBiService from './power-bi';
import type UserService from './user';

export default class ImpersonateService extends Service {
  @service('fast-api') declare fastApiService: FastApiService;
  @service('filter') declare filterService: FilterService;
  @service('kpi') declare kpiService: KpiService;
  @service('power-bi') declare powerBiService: PowerBiService;
  @service('cache-manager') declare cacheManager: CacheManagerService;
  @service('router') declare routerService: RouterService;
  @service('user') declare userService: UserService;

  accounts: ImpersonationAccount[] = [];

  @tracked impersonationAccount?: ImpersonationAccount;

  async fetchAccounts(): Promise<ImpersonationAccount[]> {
    try {
      const user = await this.userService.getUser();

      const impersonationPermissions = user.permissions.filter(
        (permission): permission is ImpersonationPermission =>
          permission.$type === 'Impersonation' && permission.granted,
      );

      const allowedAccounts: ImpersonationAccount[] = [];

      impersonationPermissions.forEach((permission) => {
        permission.properties.allowedAccountsToImpersonate.forEach(
          (account) => {
            const accountData: ImpersonationAccount = {
              id: account.id,
              displayName: account.displayName,
            };

            if (
              !allowedAccounts.some((existing) => existing.id === account.id)
            ) {
              allowedAccounts.push(accountData);
            }
          },
        );
      });

      this.accounts = allowedAccounts.sort((a, b) =>
        a.displayName.localeCompare(b.displayName),
      );

      const previousImpersonation = window.localStorage.getItem(
        IMPERSONATE_ACCOUNT_ID_KEY,
      );
      if (previousImpersonation && !this.impersonationAccount) {
        const account = this.accounts.find(
          (a) => a.id === previousImpersonation,
        );
        if (account) {
          await this.activateImpersonation(account);
        }
      }
      return this.accounts;
    } catch (error) {
      console.error('Error fetching accounts:', JSON.stringify(error));
      throw error;
    }
  }

  getImpersonatingId() {
    return (
      this.impersonationAccount?.id ||
      window.localStorage.getItem(IMPERSONATE_ACCOUNT_ID_KEY)
    );
  }

  getStoredAccountId(): string | null {
    return window.localStorage.getItem(IMPERSONATE_ACCOUNT_ID_KEY);
  }

  async activateImpersonation(account: ImpersonationAccount) {
    this.impersonationAccount = account;
    window.localStorage.setItem(IMPERSONATE_ACCOUNT_ID_KEY, account.id);
    this.userService.reloadUser();
    this.filterService.clearFiltersForImpersonation();
    await this.powerBiService.fetchTokenAndReports();

    this.routerService.refresh();
  }

  stopImpersonation() {
    this.impersonationAccount = undefined;
    window.localStorage.removeItem(IMPERSONATE_ACCOUNT_ID_KEY);

    this.filterService.clearStoredFilters();
    this.filterService.clearFiltersForImpersonation();

    this.routerService.refresh();
  }
}

// Don't remove this declaration: this is what enables TypeScript to resolve
// this service using `Owner.lookup('service:impersonate')`, as well
// as to check when you pass the service name as an argument to the decorator,
// like `@service('impersonate') declare altName: ImpersonateService;`.
declare module '@ember/service' {
  interface Registry {
    impersonate: ImpersonateService;
  }
}
